if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.authorCache = new Map(); // Cache author results by query
        this.isbnCache = new Map(); // Cache ISBN results by query
        this.focusElement = this.input;
        this.authorSearchModule = null;
        this.isbnSearchModule = null;
        this.currentQuery = '';

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(event) {
        // If search is empty, redirect to all products instead
        const query = this.getQuery();
        if (query.length === 0) {
          event.preventDefault();
          const allProductsUrl = this.dataset.allProductsUrl || '/collections/all';
          window.location.href = allProductsUrl;
        }
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.currentQuery = '';
        this.authorSearchPending = false;
        this.isbnSearchPending = false;
        this.clearAuthorResults();
        this.clearIsbnResults();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();
        this.currentQuery = this.getQuery();
        this.authorSearchPending = true; // Track that author search is in progress
        this.isbnSearchPending = true; // Track that ISBN search is in progress

        const url = this.buildUrl().toString();
        this.renderSection(url);
        this.searchAuthors(this.currentQuery);
        this.searchIsbn(this.currentQuery);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;

        // Re-inject cached author results if available for current query
        if (this.currentQuery && this.authorCache.has(this.currentQuery)) {
          const cachedAuthors = this.authorCache.get(this.currentQuery);
          if (cachedAuthors.length > 0) {
            this.injectAuthorResults(cachedAuthors, this.currentQuery);
          }
        }

        // Re-inject cached ISBN results if available for current query
        if (this.currentQuery && this.isbnCache.has(this.currentQuery)) {
          const cachedIsbn = this.isbnCache.get(this.currentQuery);
          if (cachedIsbn.length > 0) {
            this.injectIsbnResults(cachedIsbn);
          }
        }

        this.updateEmptyState();
      }

      async searchAuthors(query) {
        if (!this.authorSearchModule) {
          const url = window.theme?.assets?.authorSearch;
          if (!url) {
            this.authorSearchPending = false;
            this.updateEmptyState();
            return;
          }
          try {
            this.authorSearchModule = await import(url);
          } catch (error) {
            console.error('Failed to load author search module:', error);
            this.authorSearchPending = false;
            this.updateEmptyState();
            return;
          }
        }

        const authors = await this.authorSearchModule.searchAuthors(query, 5);

        // Cache the results
        this.authorCache.set(query, authors);
        this.authorSearchPending = false;

        // Only render if this is still the current query
        if (query === this.currentQuery) {
          this.injectAuthorResults(authors, query);
          this.updateEmptyState();
        }
      }

      injectAuthorResults(authors, query) {
        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(resultsId);
        if (!resultsContainer) return;

        // Remove any existing author results
        const existingAuthors = resultsContainer.querySelector('[data-author-results]');
        if (existingAuthors) {
          existingAuthors.remove();
        }

        if (authors.length > 0 && this.authorSearchModule) {
          // Create author results element
          const authorSection = document.createElement('div');
          authorSection.setAttribute('data-author-results', '');
          authorSection.innerHTML = this.authorSearchModule.renderAuthorResults(authors, query);

          // Find where to insert (after suggestions/queries, before products)
          // Look for suggestions section first, then collections, then insert at beginning
          const suggestionsSection = resultsContainer.querySelector('[data-suggestions]');
          const collectionsSection = resultsContainer.querySelector('[data-collections]');
          const productsSection = resultsContainer.querySelector('[data-products]');

          if (suggestionsSection) {
            // Insert after suggestions
            suggestionsSection.after(authorSection);
          } else if (collectionsSection) {
            // Insert before collections
            collectionsSection.before(authorSection);
          } else if (productsSection) {
            // Insert before products
            productsSection.before(authorSection);
          } else {
            // Insert at the beginning of results (but before empty state or sticky button)
            const emptyState = resultsContainer.querySelector('.search__empty');
            const stickyButton = resultsContainer.querySelector('.drawer__sticky');
            if (emptyState) {
              emptyState.before(authorSection);
            } else if (stickyButton) {
              stickyButton.before(authorSection);
            } else {
              resultsContainer.prepend(authorSection);
            }
          }
        }
      }

      async searchIsbn(query) {
        if (!this.isbnSearchModule) {
          const url = window.theme?.assets?.isbnSearch;
          if (!url) {
            this.isbnSearchPending = false;
            this.updateEmptyState();
            return;
          }
          try {
            this.isbnSearchModule = await import(url);
          } catch (error) {
            console.error('Failed to load ISBN search module:', error);
            this.isbnSearchPending = false;
            this.updateEmptyState();
            return;
          }
        }

        // Only search if query looks like it could be an ISBN (contains mostly numbers)
        if (!this.isbnSearchModule.looksLikeIsbn(query)) {
          this.isbnSearchPending = false;
          this.updateEmptyState();
          return;
        }

        const products = await this.isbnSearchModule.searchByIsbn(query, 3);

        // Cache the results
        this.isbnCache.set(query, products);
        this.isbnSearchPending = false;

        // Only render if this is still the current query
        if (query === this.currentQuery) {
          this.injectIsbnResults(products);
          this.updateEmptyState();
        }
      }

      injectIsbnResults(products) {
        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(resultsId);
        if (!resultsContainer) return;

        // Remove any existing ISBN results
        const existingIsbn = resultsContainer.querySelector('[data-isbn-results]');
        if (existingIsbn) {
          existingIsbn.remove();
        }

        if (products.length > 0 && this.isbnSearchModule) {
          // Create ISBN results element
          const isbnSection = document.createElement('div');
          isbnSection.setAttribute('data-isbn-results', '');
          isbnSection.innerHTML = this.isbnSearchModule.renderIsbnResults(products);

          // Insert at the very top of results (ISBN is high priority)
          const suggestionsSection = resultsContainer.querySelector('[data-suggestions]');
          if (suggestionsSection) {
            suggestionsSection.before(isbnSection);
          } else {
            resultsContainer.prepend(isbnSection);
          }
        }
      }

      updateEmptyState() {
        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(resultsId);
        const emptyState = resultsContainer?.querySelector('.search__empty');
        const authorResults = resultsContainer?.querySelector('[data-author-results]');
        const isbnResults = resultsContainer?.querySelector('[data-isbn-results]');

        if (emptyState) {
          const hasAuthors = authorResults && authorResults.innerHTML.trim() !== '';
          const hasIsbn = isbnResults && isbnResults.innerHTML.trim() !== '';
          // Hide empty state if we have authors/ISBN OR if searches are still pending
          if (hasAuthors || hasIsbn || this.authorSearchPending || this.isbnSearchPending) {
            emptyState.setAttribute('hidden', '');
          } else {
            emptyState.removeAttribute('hidden');
          }
        }
      }

      clearAuthorResults() {
        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(resultsId);
        if (resultsContainer) {
          const existingAuthors = resultsContainer.querySelector('[data-author-results]');
          if (existingAuthors) {
            existingAuthors.remove();
          }
        }
      }

      clearIsbnResults() {
        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsContainer = document.getElementById(resultsId);
        if (resultsContainer) {
          const existingIsbn = resultsContainer.querySelector('[data-isbn-results]');
          if (existingIsbn) {
            existingIsbn.remove();
          }
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}

// Search Results Page Author Integration
// When on the search results page with no Shopify results, show author matches
(function() {
  // Only run on search results page
  if (!document.querySelector('.search-section')) return;

  // Get search terms from URL
  const urlParams = new URLSearchParams(window.location.search);
  const searchTerms = urlParams.get('q');
  if (!searchTerms || searchTerms.trim().length < 2) return;

  // Check if there are no Shopify results (empty state is visible)
  const emptyState = document.querySelector('.search-section .empty-state');
  if (!emptyState) return;

  // Load and run author search
  async function searchAuthorsOnPage() {
    const url = window.theme?.assets?.authorSearch;
    if (!url) return;

    try {
      const authorModule = await import(url);
      const authors = await authorModule.searchAuthors(searchTerms.trim(), 10);

      if (authors.length > 0) {
        // Hide the empty state
        emptyState.style.display = 'none';

        // Create author results container
        const authorResultsContainer = document.createElement('div');
        authorResultsContainer.className = 'search-page-authors';
        authorResultsContainer.innerHTML = `
          <div class="grid gap-6 w-full">
            <p class="search__heading block text-xs uppercase leading-tight tracking-widest">
              ${window.theme?.strings?.authors || 'Authors'}
            </p>
            <ul class="author-results-grid grid gap-4">
              ${authors.map(author => {
                const imageHtml = author.image
                  ? `<img src="${author.image}&width=80" alt="" class="author-result__image" loading="lazy">`
                  : `<span class="author-result__placeholder">${author.name.charAt(0).toUpperCase()}</span>`;

                return `
                  <li>
                    <a class="author-result reversed-link flex items-center gap-3 text-base md:text-lg leading-tight font-medium" href="${author.url}">
                      <span class="author-result__avatar">
                        ${imageHtml}
                      </span>
                      <span>${author.name}</span>
                    </a>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        `;

        // Insert before the empty state
        emptyState.parentNode.insertBefore(authorResultsContainer, emptyState);
      }
    } catch (error) {
      console.error('Failed to load author search on results page:', error);
    }
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', searchAuthorsOnPage);
  } else {
    searchAuthorsOnPage();
  }
})();
